import { site } from '../../js/config';
import PageHeading from '../generic/PageHeading';
import type { OpeningHours, StoreWithOpeningHours } from '../model/types';
import FavoriteStoreAction from '../storelocator/FavoriteStoreAction';
import OpeningsTable from '../storelocator/OpeningsTable';
import { StoreDetailsHeader } from '../storelocator/StoreDetails';
import StructuredData from '../utils/StructuredData';
import * as style from './StoreDetailsPage.module.css';
import StoreInfoActions from './StoreInfoActions';

export interface Props {
    store: StoreWithOpeningHours;
    url: string;
}

export default function StoreDetailsPage({ store, url }: Props) {
    return (
        <>
            <div className={style.topPart}>
                <div className="section__inner-container">
                    <PageHeading heading={store.displayName} subHeading={store.locationDescription} backHref={site.findStoreUrl()} />
                    <StoreDetailsHeader store={store} showAssortment className={style.storeFinderInfo} />
                </div>
            </div>
            <div className="section__inner-container">
                {!!store.openingHours.specialOpeningList?.length && (
                    <div className={style.deviating}>
                        <h2>Avvikende åpningstider</h2>
                        <OpeningsTable openingHours={store.openingHours.specialOpeningList} deviating />
                    </div>
                )}
                <div className={style.storeDetails}>
                    <h2>Åpningstider</h2>
                    <OpeningsTable openingHours={store.openingHours.currentOpeningList} />
                    <StoreInfoActions name={store.name}>
                        <FavoriteStoreAction store={store} />
                    </StoreInfoActions>
                </div>
                <StructuredData data={{ store, url }} mapper={getStoreStructuredData} />
            </div>
        </>
    );
}

function getStoreStructuredData({ store, url }: { store: StoreWithOpeningHours; url: string }) {
    return {
        '@context': 'https://schema.org' as const,
        '@type': 'LiquorStore',
        name: store.displayName,
        address: {
            '@type': 'PostalAddress',
            streetAddress: store.address.line1,
            addressLocality: store.address.town,
            postalCode: store.address.postalCode,
            addressCountry: store.address.country?.isocode ?? null,
        },
        geo: {
            '@type': 'GeoCoordinates',
            latitude: store.geoPoint.latitude.toString(),
            longitude: store.geoPoint.longitude.toString(),
        },
        telephone: '22015000',
        openingHours: store.openingHours.weekDayOpeningList
            .filter((o) => !!o.googleOpeningHours)
            .map((o) => o.googleOpeningHours)
            .join(', '),
        specialOpeningHoursSpecification: openingHoursData(store.openingHours.specialOpeningList),
        url,
    };
}

function openingHoursData(openingHours: OpeningHours[]) {
    return openingHours
        .map((o) => ({
            date: o.day.isoDateFormat,
            closed: o.closed,
            opensAndCloses: o.hours.formattedValue.split('-').map((h) => h.trim() + ':00'),
        }))
        .map((o) => ({
            '@type': 'OpeningHoursSpecification',
            validFrom: o.date,
            validThrough: o.date,
            opens: o.closed ? undefined : o.opensAndCloses[0],
            closes: o.closed ? undefined : o.opensAndCloses[1],
        }));
}
